import { useMemo } from "react";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { formatBRL } from "@/lib/currency.ts";
import { useMeasurementHistory } from "../../hooks/use-measurement-history.ts";
import type { HistoryEntry, HistoryTarget } from "../../types.ts";
import {
	formatMeasuredOn,
	formatProgressPercent,
	formatSignedBRL,
	formatSignedPercent,
} from "../../utils/format.ts";
import { buildHistory } from "../../utils/history.ts";

const SKELETON_ROWS = ["first", "second", "third"];

function HistoryRow({
	entry,
	showLeaf,
	showMoney,
}: {
	entry: HistoryEntry;
	showLeaf: boolean;
	showMoney: boolean;
}) {
	return (
		<TableRow className="history-dialog__row">
			<TableCell className="history-dialog__date">
				{formatMeasuredOn(entry.measuredOn)}
			</TableCell>
			{showLeaf && (
				<TableCell className="history-dialog__leaf">
					<span className="history-dialog__leaf-item">
						{entry.stageItemNumber}
					</span>
					<span className="history-dialog__leaf-name">{entry.stageName}</span>
				</TableCell>
			)}
			<TableCell className="history-dialog__number">
				{formatProgressPercent(entry.progressPercent)}
			</TableCell>
			<TableCell className="history-dialog__delta">
				{formatSignedPercent(entry.percentDelta)}
			</TableCell>
			{showMoney && (
				<TableCell className="history-dialog__number">
					{entry.measuredCents === undefined
						? "—"
						: formatBRL(entry.measuredCents)}
				</TableCell>
			)}
			{showMoney && (
				<TableCell className="history-dialog__delta">
					{entry.centsDelta === undefined
						? "—"
						: formatSignedBRL(entry.centsDelta)}
				</TableCell>
			)}
			<TableCell className="history-dialog__author">
				{entry.measuredByName ?? "—"}
			</TableCell>
			<TableCell className="history-dialog__note">
				{entry.note || "—"}
			</TableCell>
		</TableRow>
	);
}

export function HistoryDialog({
	open,
	onOpenChange,
	constructionId,
	target,
	valueByStage,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	constructionId: string;
	target: HistoryTarget | null;
	valueByStage?: ReadonlyMap<string, number>;
}) {
	const history = useMeasurementHistory(
		open ? constructionId : undefined,
		target,
	);
	const entries = useMemo(
		() => (history.data ? buildHistory(history.data, valueByStage) : []),
		[history.data, valueByStage],
	);
	const showLeaf = !!target && !target.isLeaf;
	const showMoney = !!valueByStage;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="history-dialog">
				<DialogHeader>
					<DialogTitle>Histórico de medições</DialogTitle>
					<DialogDescription>
						{target ? `${target.itemNumber} · ${target.name}` : ""}
					</DialogDescription>
				</DialogHeader>
				<div className="history-dialog__body">
					{history.isPending ? (
						<div className="history-dialog__skeleton">
							{SKELETON_ROWS.map((row) => (
								<Skeleton key={row} className="history-dialog__skeleton-row" />
							))}
						</div>
					) : history.isError ? (
						<div className="card-state">
							<p>Não foi possível carregar o histórico.</p>
							<Button variant="outline" onClick={() => history.refetch()}>
								Tentar novamente
							</Button>
						</div>
					) : entries.length === 0 ? (
						<p className="history-dialog__empty">Nenhuma medição registrada.</p>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Data</TableHead>
									{showLeaf && <TableHead>Item</TableHead>}
									<TableHead className="history-dialog__number">
										Acumulado
									</TableHead>
									<TableHead className="history-dialog__number">
										Variação
									</TableHead>
									{showMoney && (
										<TableHead className="history-dialog__number">
											Medido (R$)
										</TableHead>
									)}
									{showMoney && (
										<TableHead className="history-dialog__number">
											Variação (R$)
										</TableHead>
									)}
									<TableHead>Autor</TableHead>
									<TableHead>Nota</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{entries.map((entry) => (
									<HistoryRow
										key={entry.id}
										entry={entry}
										showLeaf={showLeaf}
										showMoney={showMoney}
									/>
								))}
							</TableBody>
						</Table>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
